import preQualificationTypes from '@/utilities/pq-types'
import preQualificationInternational from '@/assets/json/pqInternational.json'
import _ from 'lodash'
import { formatUSHHINumberRange, parseLucidHHIPrecodeRange } from '@/components/AudienceBuilder/Audience'

const pqTypes = Object.assign(preQualificationTypes, preQualificationInternational)

const keyMap = {
  toLucid: {},
  toReadable: {}
}

const criteriaOrdering = [
  'Age',
  'Education',
  'Gender',
  'Household Income',
  'State',
  'Parental Status',
  'Relationship Status',
  'Household Type',
  'Employment',
  'Industry',
  '# of Employees',
  'Company Dept.',
  'Job Title',
  'B2B Decisions',
  'Additional Criteria',
  'International',
  'Device',
  'Big Purchases'
]

Object.keys(pqTypes).forEach((key) => {
  keyMap.toLucid[key] = pqTypes[key].lucidKey
  keyMap.toReadable[pqTypes[key].lucidKey] = key
})
Object.freeze(keyMap)

const format = {
  translate (pqs, customCriteria, options = {}) {
    const formattedPQs = {}
    Object.keys(customCriteria).forEach((key) => {
      if (key === 'Additional Criteria') {
        if (!options.lucid) formattedPQs[key] = customCriteria[key]
        return
      }
      const updatedKey = options.lucid ? pqs[key].lucidKey : key
      formattedPQs[updatedKey] = key === 'Age'
        ? this.createPrecodeRange(customCriteria[key])
        : this.toPrecodes(pqs, key, customCriteria[key])
    })
    return formattedPQs
  },
  createPrecodeRange (originalRange) {
    const min = originalRange[0]
    const max = originalRange[originalRange.length - 1]
    const range = []
    for (let i = min; i <= max; i++) {
      range.push(parseInt(i).toString())
    }
    // ["1", "2", "3", "4", "5"]
    return range
  },
  // strip out anything unnecessary for drop.payload.segment
  forCard (audience) {
    if (typeof audience === 'string') return audience
    return {
      title: audience.title,
      id: audience.id
    }
  },
  forBuilder (audienceId, audience, options = {}) {
    const criteria = {}
    if (audience.segment.custom) {
      criteria['Additional Criteria'] = audience.segment.custom
    }
    if (audience.segment.international && audience.segment.countries) {
      criteria.International = audience.segment.countries
    }
    if (audience.segment.lucid) {
      Object.keys(audience.segment.lucid).forEach((k) => {
        if (audience.segment.lucid[k].length > 0) {
          const builderKey = keyMap.toReadable[k]
          criteria[builderKey] = audience.segment.lucid[k]
        }
      })
    }
    let demoQualifies = audience.segment.demoQualifies
    if (options.questions && !demoQualifies) {
      demoQualifies = options.questions.some((q) => {
        return q.payload && q.payload.answers && q.payload.answers.some((a) => {
          return a.set_tag && a.set_tag.some((tag) => {
            return tag.startsWith('SUCCESS_')
          })
        })
      })
    }
    return {
      title: audience.name,
      criteria: criteria,
      id: audienceId,
      segid: audience.name === 'US Adults' ? 'gp' : audience.segment.id,
      demoQualifies: demoQualifies,
      accountId: audience.account_id
    }
  },
  forListingCriteria (customCriteria) {
    if (!customCriteria) return []

    const readableKeys = {}
    Object.keys(customCriteria).forEach(criteriaKey => {
      readableKeys[keyMap.toReadable[criteriaKey]] = customCriteria[criteriaKey]
    })

    const orderedReadableKeys = _.intersection(criteriaOrdering, Object.keys(readableKeys || {}))

    return orderedReadableKeys.map(readableKey => {
      const originalValue = readableKeys[readableKey]

      switch (readableKey) {
        case 'Age':
          return {
            key: readableKey,
            value: blurb.displayAge(originalValue)
          }
        case 'Household Income':
          return {
            key: readableKey,
            value: blurb.displayHHI(originalValue)
          }
        default:
          return {
            key: readableKey,
            value: blurb.displayTextFromPreCodes(readableKey, originalValue)
          }
      }
    })
  },
  forRR (customCriteria) {
    const segment = {
      lucid: this.translate(preQualificationTypes, customCriteria, { lucid: true }),
      custom: customCriteria['Additional Criteria'] || false,
      countries: undefined
    }
    if (segment.lucid.International) {
      segment.countries = segment.lucid.International
      delete segment.lucid.International
    }
    return segment
  },
  fromLegacyToBuilder (audience) {
    return {
      title: audience.name,
      description: (audience.payload && audience.payload.description) || '',
      legacy: true,
      criteria: {}
    }
  },
  toPrecodes (pqs, key, range) {
    const options = pqs[key].options
    let precodes = []
    if (key === 'Household Income') {
      const convertedRange = [
        slider.valToPrecode(parseInt(range[0]), false),
        slider.valToPrecode(parseInt(range[range.length - 1]), true)
      ]
      precodes = this.createPrecodeRange(convertedRange)
    } else {
      options.forEach((opt) => {
        if (range.includes(opt.title)) {
          precodes = precodes.concat(opt.precodes)
        }
      })
    }
    return precodes
  },
  normalizeAudience (audience) {
    return typeof audience === 'object' ? audience : { title: audience, id: audience, isLegacy: true }
  }
}

const blurb = {
  translate (criteria) {
    let str = ''
    Object.keys(criteria).forEach((key) => {
      if (str.length > 0) str += ',\n'
      str += `${key}: `
      /* es-lint disable indent */
      str += key === 'Additional Criteria'
        ? criteria[key]
        : key === 'Age'
          ? this.displayAge(criteria[key])
          : key === 'Household Income'
            ? this.displayHHI(criteria[key])
            : this.displayTextFromPreCodes(key, criteria[key])
      /* es-lint enable indent */
    })
    return str
  },
  displayTextFromPreCodes (key, codes) {
    const criteria = pqTypes[key]
    if (!criteria) return 'Unknown Pre-Qualifications'

    const blurb = []
    criteria.options.forEach((opt) => {
      if (opt.precodes.filter(x => { return codes.includes(x) }).length > 0) blurb.push(opt.title)
    })

    return blurb.join(', ')
  },
  displayText (criteria, options) {
    if (!criteria) return 'Unknown Pre-Qualifications'

    return options.join(', ')
  },
  displayAge (range) {
    const min = range[0]
    const max = range[range.length - 1]
    return `${min} to ${max} years old`
  },
  displayHHI (range) {
    let firstHHI = this.translateHHI(parseInt(range[0]), true)
    firstHHI = firstHHI === '<5,000' ? '<$5,000' : `$${firstHHI}`
    const lastHHI = this.translateHHI(parseInt(range[range.length - 1]))
    return `${firstHHI} to $${lastHHI}`
  },
  translateHHI (code, isLowerBound = false) {
    let income
    if (code > 20 || code === 1) {
      switch (code) {
        case 26:
          income = isLowerBound ? '250,000' : '250,000+'
          break
        case 25:
          income = isLowerBound ? '200,000' : '250,000'
          break
        case 24:
          income = isLowerBound ? '175,000' : '200,000'
          break
        case 23:
          income = isLowerBound ? '150,000' : '175,000'
          break
        case 22:
          income = isLowerBound ? '125,000' : '150,000'
          break
        case 21:
          income = isLowerBound ? '100,000' : '125,000'
          break
        case 1:
          income = '<5,000'
      }
      return income
    } else {
      income = (isLowerBound ? code - 1 : code) * 5000
      return this.formatNumberWithCommas(income.toString())
    }
  },
  formatNumberWithCommas (x) {
    var parts = x.split('.')
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    return parts.join(',')
  }
}

const slider = {
  getSliderRange (range) {
    // range = [min, max]
    let min, max
    range.forEach((i) => {
      const precode = parseInt(i)
      min = min === undefined || min > precode ? precode : min
      max = max === undefined || max < precode ? precode : max
    })
    // [18, 75]
    return [min, max]
  },
  valToPrecode (val, max) {
    /* eslint-disable indent */
    return max ? (
      val === 255000 ? 26
        : val <= 250000 && val > 200000 ? 25
          : val <= 200000 && val > 175000 ? 24
            : val <= 175000 && val > 150000 ? 23
              : val <= 150000 && val > 125000 ? 22
                : val <= 125000 && val > 100000 ? 21
                  : val === 100000 ? 20
                    : val === 0 ? 1
                      : (val / 5000)
    ) : (
      val >= 250000 ? 26
        : val < 250000 && val >= 200000 ? 25
          : val < 200000 && val >= 175000 ? 24
            : val < 175000 && val >= 150000 ? 23
              : val < 150000 && val >= 125000 ? 22
                : val < 125000 && val >= 100000 ? 21
                  : val === 0 ? 1
                    : (val / 5000) + 1
    )
    /* eslint-enable indent */
  },
  precodeToVal (val, max) {
    /* eslint-disable indent */
    return max ? (
      val === 26 ? 255000
        : val === 25 ? 250000
          : val === 24 ? 200000
            : val === 23 ? 175000
              : val === 22 ? 150000
                : val === 21 ? 125000
                  : val === 20 ? 100000
                    : val === 0 ? 0
                      : (val * 5000)
    ) : (
      val === 26 ? 250000
        : val === 25 ? 200000
          : val === 24 ? 175000
            : val === 23 ? 150000
              : val === 22 ? 125000
                : val === 21 ? 100000
                  : val === 20 ? 100000
                    : val === 0 ? 0
                      : ((val - 1) * 5000)
    )
    /* eslint-enable indent */
  }
}

export {
  slider,
  blurb,
  format
}
